import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import api from '../../utils/api';
import { Building2, Calendar, Users, TrendingUp, Clock, MapPin } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const AdminOverviewPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get('/admin/overview')
      .then((res) => setData(res.data))
      .catch(() => setError('Could not load platform overview.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-24 text-gray-500 dark:text-gray-400">
        {error || 'No data available.'}
      </div>
    );
  }

  const { stats, credits, monthlyEvents, monthlySignups, upcomingEvents, topOrganizers } = data;

  const statCards = [
    { label: 'Organizers', value: stats.organizers, icon: Building2, color: 'bg-blue-100 dark:bg-blue-900/20', iconColor: 'text-blue-600' },
    { label: 'Total Events', value: stats.totalEvents, icon: Calendar, color: 'bg-purple-100 dark:bg-purple-900/20', iconColor: 'text-purple-600' },
    { label: 'Total Guests', value: stats.totalGuests, icon: Users, color: 'bg-green-100 dark:bg-green-900/20', iconColor: 'text-green-600' },
    { label: 'Attendance Rate', value: `${stats.attendanceRate}%`, icon: TrendingUp, color: 'bg-orange-100 dark:bg-orange-900/20', iconColor: 'text-orange-600' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Platform Overview</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Stats and analytics across every organizer on EventPulse.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="p-6 hover:shadow-md transition-smooth">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Card className="p-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">Upcoming / Completed Events</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {stats.upcomingEvents} / {stats.completedEvents}
          </p>
        </Card>
        <Card className="p-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">Registered Guests</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.totalRegistered}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stats.totalAttended} checked in</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">Staff Accounts</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.staff}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">across all organizers</p>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Events Created (Last 6 Months)
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={monthlyEvents}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" allowDecimals={false} />
              <Tooltip
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                cursor={{ fill: '#f3f4f6' }}
              />
              <Bar dataKey="count" name="Events" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Organizer Signups (Last 6 Months)
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={monthlySignups}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
              <Line type="monotone" dataKey="count" name="Signups" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Upcoming Events</h2>
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No upcoming events across the platform.</p>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map((event) => (
                <div key={event.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">{event.name}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">by {event.organizer}</p>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(event.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{event.time}</span>
                        </div>
                        <div className="flex items-center gap-1 min-w-0">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span className="truncate">{event.venue}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {event.registeredGuests}/{event.totalGuests}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">registered</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Top Organizers</h2>
          {topOrganizers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No organizers yet.</p>
          ) : (
            <div className="space-y-3">
              {topOrganizers.map((org, i) => (
                <div
                  key={org.id}
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-sm font-semibold text-gray-700 dark:text-gray-300 shrink-0">
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{org.company || org.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{org.email}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{org.eventsCount} events</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{org.guestsCount} guests</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Credits Economy</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">Free Credits Outstanding</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{credits.freeCreditsOutstanding}</p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">Purchased Credits Outstanding</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{credits.purchasedCreditsOutstanding}</p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Credits Purchased</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{credits.creditsPurchasedTotal}</p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Credits Spent</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{credits.creditsSpentTotal}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminOverviewPage;
